export type AuditKey = { kind: 'digit'; key: string } | { kind: 'submit' };

const DIGITS: ReadonlySet<string> = new Set(['1', '2', '3', '4', '5', '6', '7']);
/** Elements that take typed keys for themselves (the same set as `projectGraph/shortcuts.ts`). */
const TYPING_TAGS: ReadonlySet<string> = new Set(['INPUT', 'TEXTAREA', 'SELECT']);
/** Enter on a focused button or link presses it; it is not the Submit shortcut there. */
const PRESSABLE_TAGS: ReadonlySet<string> = new Set(['BUTTON', 'A']);

/** A dialog over the page: the full-screen page preview or a Headless UI dialog. */
const MODAL_SELECTOR = '[role="dialog"], [aria-modal="true"]';

/**
 * Whether a dialog is open anywhere in `doc`. The focus can stay on `body` under an open dialog (the page preview
 * has no focus trap), so the key target alone does not show it.
 */
export const isModalOpen = (doc: { querySelector(selector: string): unknown }): boolean =>
  doc.querySelector(MODAL_SELECTOR) !== null;

/**
 * The audit shortcut a key press asks for, or `null`: for any other key, a key pressed with Ctrl, Cmd or Alt
 * (`modified`), a key typed into a field (`targetTag`), a key inside an element that owns its keys (`ownsKeys`),
 * and any key while a dialog is open (`modalOpen`), so the item behind the page preview is never labelled.
 */
export function auditKeyFor(
  key: string,
  targetTag: string,
  {
    modified = false,
    ownsKeys = false,
    modalOpen = false,
  }: { modified?: boolean; ownsKeys?: boolean; modalOpen?: boolean } = {},
): AuditKey | null {
  const tag = targetTag.toUpperCase();
  if (modified || ownsKeys || modalOpen || TYPING_TAGS.has(tag)) return null;
  if (DIGITS.has(key)) return { kind: 'digit', key };
  if (key === 'Enter' && !PRESSABLE_TAGS.has(tag)) return { kind: 'submit' };
  return null;
}
